/**
 * @fileoverview Mixin que centraliza el manejo de bloqueos y errores del flujo de login.
 * Extrae de Login.vue las responsabilidades de:
 *  - Bloqueo permanente (PERMANENTE)
 *  - Bloqueo por intentos fallidos (BLOQUEO_INTENTOS)
 *  - Intentos fallidos genéricos
 *  - Errores genéricos HTTP
 *  - Navegación a recuperación/registro desde modales de error
 * @module mixins/login-lock-handler.mixin
 */

// import DocumentRules from '../interfaces/documentRules'
import Links from '../interfaces/links'
// import { isPermanentLock } from '../interfaces/loginResponse'

export default {
  data () {
    return {
      links: Links,
      lockInfo: {
        isLocked: false,
        lockType: null,
        remainingMinutes: null,
        unlockDate: null,
        showCountdown: false
      },
      failedAttempt: {
        showModal: false,
        title: '',
        body: '',
        secondaryButtonText: 'Registrarme',
        secondaryAction: 'register'
      },
      lockMessage: ''
    }
  },
  methods: {
    /**
     * Maneja el bloqueo permanente de la cuenta.
     * Dispara eventos GTM/Amplitude y muestra SweetAlert.
     * @param {Object} responseData - Datos de respuesta del backend
     */
    handlePermanentLock (responseData) {
      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Bloqueo permanente - Cuenta bloqueada'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Modal',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: 'Bloqueo permanente - Cuenta bloqueada'
      })
      this.$amplitude.getInstance().logEvent('Cuenta Bloqueada Permanentemente', {
        'Tipo Bloqueo': 'PERMANENTE',
        'DNI': this.txtUserLogin
      })

      this.$swal({
        imageUrl: this.icons.error,
        titleText: '¡Lo sentimos!',
        html: responseData.mensajeRespuesta ||
          `Por tu seguridad, tu acceso ha sido bloqueado. Para continuar, comunícate con nuestros <a href="${this.links.WhatsApp}" target="_blank" class="omnesMedium">canales de atención al cliente</a>.`,
        confirmButtonText: 'Entendido',
        allowOutsideClick: false
      })

      this.lockInfo.isLocked = true
      this.lockInfo.lockType = 'PERMANENTE'
    },

    /**
     * Maneja el bloqueo por intentos fallidos (BLOQUEO_INTENTOS).
     * Activa LockAttemptsModal para indicar que la cuenta quedó bloqueada.
     * @param {Object} responseData - Datos de respuesta del backend
     */
    handleTemporaryLock (responseData) {
      if (responseData.tipoBloqueo !== 'BLOQUEO_INTENTOS') return

      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Bloqueo por intentos fallidos - Cuenta bloqueada'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Modal',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: 'Bloqueo por intentos fallidos - Cuenta bloqueada'
      })
      this.$amplitude.getInstance().logEvent('Cuenta Bloqueada por Intentos Fallidos', {
        'Tipo Bloqueo': 'BLOQUEO_INTENTOS',
        'DNI': this.txtUserLogin
      })

      this.lockMessage = 'Verifica que tus datos estén correctamente ingresados. Si ya tienes una cuenta, recupera tu acceso desde "Olvidé mi contraseña". Si es tu primera vez aquí, puedes registrarte para acceder a la Zona Segura después de las 24 horas de la compra.'
      this.lockInfo.showCountdown = true
      this.lockInfo.lockType = 'BLOQUEO_INTENTOS'
      this.lockInfo.isLocked = true
    },

    /**
     * Maneja cualquier intento fallido de login con mensaje genérico anti-enumeración.
     * Se usa tanto para el 1er/2do intento como para BLOQUEO_INTENTOS.
     * @param {Object} responseData - Datos de respuesta del backend
     */
    handleFailedAttempt (responseData) {
      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Error de ingreso - Mensaje genérico'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Modal',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: 'Error de ingreso - Mensaje genérico'
      })
      this.$amplitude.getInstance().logEvent('Intento de Login Fallido', {
        'Intentos Restantes': (responseData && responseData.intentosRestantes) || 0,
        'DNI': this.txtUserLogin
      })

      this.failedAttempt.showModal = true
      this.failedAttempt.title = 'Es posible que el proceso de registro esté pendiente'
      this.failedAttempt.body = 'Verifica que tus datos estén correctamente ingresados. Si es tu primera vez aquí, puedes completar tu registro para acceder a la Zona Segura después de las 24 horas de la compra.'
      this.failedAttempt.secondaryButtonText = 'Registrarme'
      this.failedAttempt.secondaryAction = 'registrate'
    },

    /**
     * Muestra un SweetAlert genérico para errores de infraestructura.
     * @param {string} title - Título del modal
     * @param {string} message - Mensaje HTML del modal
     */
    handleGenericError (title, message) {
      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Error de ingreso - Error genérico'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Modal',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: 'Error de ingreso - Error genérico'
      })

      this.$swal({
        imageUrl: this.icons.error,
        titleText: title,
        html: message,
        confirmButtonText: 'Entendido'
      })
    },

    /**
     * Cierra el modal de intento fallido y resetea su estado.
     */
    closeFailedAttemptModal () {
      this.failedAttempt.showModal = false
      this.failedAttempt.title = ''
      this.failedAttempt.body = ''
      this.failedAttempt.secondaryButtonText = 'Registrarme'
      this.failedAttempt.secondaryAction = 'register'
    },

    /**
     * Permite al usuario volver a intentar el login cerrando el modal de error.
     */
    retryLogin () {
      this.closeFailedAttemptModal()
    },

    /**
     * Ejecuta la acción secundaria del modal de intento fallido:
     * navega a recuperación de contraseña o a registro según el estado.
     */
    handleFailedAttemptSecondaryAction () {
      if (this.failedAttempt.secondaryAction === 'recovery') {
        this.goToRecoveryPassword()
        return
      }
      this.goToRegister()
    },

    /**
     * Navega a la pantalla de registro cerrando los modales de bloqueo.
     */
    goToRegister () {
      this.closeFailedAttemptModal()
      this.lockInfo.showCountdown = false
      this.createAccount()
    },

    /**
     * Convierte **texto** a <strong>texto</strong> para mostrar negritas en HTML.
     * @param {string} text - Texto con formato markdown básico
     * @returns {string} Texto con etiquetas HTML
     */
    formatBoldText (text) {
      if (!text) return ''
      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    },

    /**
     * Maneja errores HTTP 500/504 y errores desconocidos del servidor.
     * @param {number} status - Código de status HTTP
     */
    handleHttpError (status) {
      const errorMessages = {
        500: {
          label: 'Error de ingreso - Server error 500',
          text: 'Tenemos algunos inconvenientes. Por favor, inténtalo nuevamente en unos minutos.'
        },
        504: {
          label: 'Error de ingreso - Server error 504',
          text: 'Tenemos algunos inconvenientes para brindarte el detalle de tu cuenta. Por favor, verifica tu conexión a Internet e inténtalo nuevamente.'
        },
        default: {
          label: 'Error de ingreso - Unknow server error',
          text: 'Tenemos algunos inconvenientes para brindarte el detalle de tu cuenta. Por favor, verifica tu conexión a Internet e inténtalo nuevamente.'
        }
      }

      const config = errorMessages[status] || errorMessages.default
      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: config.label
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Botón',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: config.label
      })
      this.$swal({
        imageUrl: this.icons.aviso,
        titleText: 'Estimado cliente',
        text: config.text,
        confirmButtonText: 'Entendido'
      })
    },

    /**
     * Maneja el caso de documento inválido mostrando el mensaje correcto.
     */
    handleInvalidDocument () {
      this.$gtm.trackEvent({
        event: 'virtualEvent',
        category: 'Zona Segura',
        action: 'Inicio de sesión',
        label: 'Error de ingreso - Numero de documento invalido'
      })
      this.$gtm.trackEvent({
        event: 'select_button',
        'Nombre Sección': 'Zona Segura',
        TipoElemento: 'Botón',
        TipoAccion: 'Errores en Inicio de Sesión',
        CTA: 'Error de ingreso - Numero de documento invalido'
      })

      if (!this.form.numeroDocumento && !this.form.clave) {
        this.$swal({
          titleText: 'Estimado cliente',
          text: 'Por favor, ingresa tu número de documento y contraseña.',
          confirmButtonText: 'Entendido'
        }).then(() => { this.loading = false })
        return
      }
      if (!this.form.numeroDocumento) {
        this.$swal({
          titleText: 'Estimado cliente',
          text: 'Por favor, ingresa tu número de documento.',
          confirmButtonText: 'Entendido'
        }).then(() => { this.loading = false })
        return
      }
      this.$swal({
        titleText: 'Estimado cliente',
        text: 'Los datos ingresados son incorrectos. Por favor, inténtalo nuevamente.',
        confirmButtonText: 'Entendido'
      }).then(() => { this.loading = false })
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/login-lock-handler.mixin.js